import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { currentDateTime, wtFormatDate } from 'src/helpers/commons/moment'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'
import { TYPE_GROUP, TYPE_TOTAL } from 'src/models/pnlConfig'

const user = wtGetFromLocalStorage('authUser')
const BRANCH_COLUMN_COLORS = [
  { text: [15, 61, 117] },
  { text: [22, 101, 52] },
  { text: [154, 52, 18] },
  { text: [91, 33, 182] },
  { text: [153, 27, 27] },
  { text: [21, 94, 117] },
]

const flattenGpBranchLayouts = (branchLayouts = []) =>
  (branchLayouts || []).flatMap((branchLayout) => {
    const columnCount = branchLayout?.columns?.length || 0

    return (branchLayout?.columns || []).map((column) => ({
      ...column,
      branch_label: branchLayout.branch_label,
      full_label:
        columnCount > 1
          ? `${branchLayout.branch_label} - ${column.label}`
          : branchLayout.branch_label,
    }))
  })

const usesPdfSubBranchLayout = (branchLayouts = []) =>
  (branchLayouts || []).some((branchLayout) => Number(branchLayout?.uses_sub_branch || 0) === 1)

const getPdfNotesColumns = (branchLayouts = []) =>
  (branchLayouts || []).map((branchLayout) => ({
    key: `branch_total_${branchLayout.branch_id}`,
    label: branchLayout.branch_label,
    branch_id: branchLayout.branch_id,
    branch_label: branchLayout.branch_label,
  }))

const isPdfRowVisible = (row) => {
  const hasIsDisplay = Object.prototype.hasOwnProperty.call(row, 'is_display')
  const hasIsShow = Object.prototype.hasOwnProperty.call(row, 'is_show')

  const isDisplayVisible = !hasIsDisplay || row.is_display === true || row.is_display === 1 || row.is_display === '1'
  const isShowVisible = !hasIsShow || row.is_show === true || row.is_show === 1 || row.is_show === '1'
  const isPdfVisible = row.show_on_pdf !== false && row.show_on_pdf !== 0 && row.show_on_pdf !== '0'

  return isDisplayVisible && isShowVisible && isPdfVisible
}

const isPercentageRow = (row) => row?.is_percentage === true || row?.is_percentage === 1 || row?.is_percentage === '1'

const isLabelRow = (row) => row?.is_label === true || row?.is_label === 1 || row?.is_label === '1'

const hasNonZeroPdfValue = (row) => {
  const branchValues = Object.keys(row || {})
    .filter((key) => key.startsWith('current_year_balance_'))
    .map((key) => Number(row[key] || 0))

  const totalValue = Number(row?.total || 0)

  return [...branchValues, totalValue].some((value) => value !== 0)
}

const getPdfBranchAggregateValue = (row, branchLayout) =>
  (branchLayout?.columns || []).reduce((sum, column) => sum + Number(row?.[column.key] || 0), 0)

const shouldKeepPdfRow = (row, children = []) => {
  if (isLabelRow(row)) {
    return true
  }

  return hasNonZeroPdfValue(row) || children.length > 0
}

const normalizePdfRows = (rows = []) =>
  rows
    .filter((row) => isPdfRowVisible(row))
    .map((row) => {
      const normalizedChildren = Array.isArray(row.children) ? normalizePdfRows(row.children) : []

      if (!shouldKeepPdfRow(row, normalizedChildren)) {
        return null
      }

      return {
        ...row,
        children: normalizedChildren,
      }
    })
    .filter(Boolean)

const formatPdfValue = (value, row) => {
  if (isLabelRow(row)) {
    return ''
  }

  if (isPercentageRow(row)) {
    return `${Number(value || 0).toFixed(2)} %`
  }

  return wtFormatMoney(Math.round(value || 0), '', true, 0)
}

const Pdf = async (gpReportData, asOf, branchLayouts) => {
  const generatePdf = (reportData, reportAsOf, layouts) => {
    const reportColumns = flattenGpBranchLayouts(layouts)
    const notesColumns = getPdfNotesColumns(layouts)
    const showSubBranchesOnStatementPage = usesPdfSubBranchLayout(layouts)
    const visibleReportData = normalizePdfRows(reportData || []).filter(
      (row) => shouldKeepPdfRow(row, row.children || []),
    )

    if (!visibleReportData || visibleReportData.length <= 0) {
      lzNotifyErrorMessageFromResult('No Data found!')
      return
    }

    const printedAt = currentDateTime()

    const isLandscape = reportColumns.length > 2
    const doc = new jsPDF({
      orientation: isLandscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    })

    const totalPagesExp = '{total_pages_count_string}'
    const logo = 'assets/wil-icon.png'
    const pageWidth = doc.internal.pageSize.width ? doc.internal.pageSize.width : doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.height ? doc.internal.pageSize.height : doc.internal.pageSize.getHeight()
    const usableWidth = pageWidth - 20
    const noteColumnWidth = 15
    const statementNumericColumnCount = reportColumns.length + 1
    const notesNumericColumnCount = notesColumns.length + 1
    const statementFirstColumnWidth = getStatementFirstColumnWidth()
    const notesFirstColumnWidth = getNotesFirstColumnWidth()
    const statementNumericColumnWidth = getStatementNumericColumnWidth()
    const notesNumericColumnWidth = getNotesNumericColumnWidth()
    const statementValueFontSize = showSubBranchesOnStatementPage ? 8 : 10
    const statementLabelFontSize = showSubBranchesOnStatementPage ? 8 : 9
    const notesValueFontSize = 12
    const statementBranchHeaderFontSize = showSubBranchesOnStatementPage ? 9 : 12
    const notesBranchHeaderFontSize = 9

    function getStatementNumericColumnWidth() {
      const statementRemainingWidth = usableWidth - statementFirstColumnWidth
      return statementRemainingWidth / statementNumericColumnCount
    }

    function getNotesNumericColumnWidth() {
      const notesRemainingWidth = usableWidth - notesFirstColumnWidth - noteColumnWidth
      return notesRemainingWidth / notesNumericColumnCount
    }

    function getStatementFirstColumnWidth() {
      if (isLandscape) {
        if (reportColumns.length >= 6) {
          return 36
        }
        if (reportColumns.length >= 4) {
          return 42
        }
        return 48
      }

      if (reportColumns.length === 2) {
        return 40
      }

      return 46
    }

    function getNotesFirstColumnWidth() {
      if (isLandscape) {
        if (notesColumns.length >= 6) {
          return 49
        }
        if (notesColumns.length >= 4) {
          return 55
        }
        return 61
      }

      if (notesColumns.length === 2) {
        return 51
      }

      return 59
    }

    const header = () => {
      doc.setFontSize(11)
      doc.setTextColor('#3c4b64')
      doc.setFont('Helvetica', 'bold')
      doc.addImage(logo, 'PNG', 5, 4, 25, 25)
      doc.line(32, 6, 32, 25)

      doc.text('WAH', 35, 10, { align: 'left' })
      doc.text('INDUSTRIES', 35, 15)
      doc.text('LIMITED', 35, 20)
      doc.setFontSize(9)
      doc.text('www.wahindustries.com', 35, 25)
      doc.setFontSize(11)
      doc.text('STATEMENT OF GP REPORT', pageWidth - (isLandscape ? 92 : 75), 15, { align: 'left' })
      doc.text('AS OF ' + wtFormatDate(reportAsOf, 'MMMM D, Y'), pageWidth - (isLandscape ? 92 : 75), 22, {
        align: 'left',
      })

      doc.line(10, 30, pageWidth - 10, 30)
    }

    const headerNotes = () => {
      doc.setFontSize(11)
      doc.setTextColor('#3c4b64')
      doc.setFont('Helvetica', 'bold')
      doc.addImage(logo, 'PNG', 5, 4, 25, 25)
      doc.line(32, 6, 32, 25)

      doc.text('WAH', 35, 10, { align: 'left' })
      doc.text('INDUSTRIES', 35, 15)
      doc.text('LIMITED', 35, 20)
      doc.setFontSize(9)
      doc.text('www.wahindustries.com', 35, 25)
      doc.setFontSize(11)
      doc.text('NOTES TO THE GP REPORT', pageWidth - (isLandscape ? 86 : 70), 15, { align: 'left' })
      doc.text('AS OF ' + wtFormatDate(reportAsOf, 'MMMM D, Y'), pageWidth - (isLandscape ? 86 : 70), 22, {
        align: 'left',
      })

      doc.line(10, 30, pageWidth - 10, 30)
    }

    const footer = () => {
      const pageNo = doc.internal.getNumberOfPages()

      doc.setFontSize(10)
      doc.setTextColor('black')
      doc.setFont('Helvetica', 'normal')

      const footerLineY = pageHeight - 15
      const footerMetaY = pageHeight - 5

      doc.line(14, footerLineY, pageWidth - 14, footerLineY)

      let str = 'Page ' + pageNo
      if (typeof doc.putTotalPages === 'function') {
        str = str + ' of ' + totalPagesExp
      }

      doc.setTextColor('black')
      doc.setFontSize(9)
      doc.text(str, pageWidth - 10, footerMetaY, { align: 'right' })
      doc.text('Printed By ', 20, footerMetaY, { align: 'left' })
      doc.setTextColor('blue')
      doc.text(user ? user.name : '', 37, footerMetaY, { align: 'left' })
    }

    const drawPrintedAt = () => {
      const footerLineY = pageHeight - 15
      const printedAtY = footerLineY - 5

      doc.setFontSize(10)
      doc.setTextColor('black')
      doc.setFont('Helvetica', 'normal')
      doc.text('This report is printed through ERP at ', 20, printedAtY)

      doc.setTextColor('#0202f7')
      doc.setFont('Helvetica', 'bold')
      doc.text(printedAt, 84, printedAtY)
    }

    const statementHeadRows = () => {
      const row = [{ content: '', styles: { halign: 'center', cellWidth: statementFirstColumnWidth } }]

      reportColumns.forEach((branch, index) => {
        const branchColor = BRANCH_COLUMN_COLORS[index % BRANCH_COLUMN_COLORS.length]
        const statementColumnLabel =
          showSubBranchesOnStatementPage && branch.branch_label && branch.label
            ? `${branch.branch_label}\n${branch.label}`
            : branch.full_label

        row.push({
          content: statementColumnLabel,
          styles: {
            halign: 'center',
            valign: 'middle',
            cellWidth: statementNumericColumnWidth,
            textColor: branchColor.text,
            fontSize: statementBranchHeaderFontSize,
          },
        })
      })

      row.push({
        content: 'Total',
        styles: {
          halign: 'center',
          cellWidth: statementNumericColumnWidth,
          textColor: [17, 24, 39],
          fontSize: statementBranchHeaderFontSize,
        },
      })

      return [row]
    }

    const notesHeadRows = () => {
      const row = [
        { content: '', styles: { halign: 'center', cellWidth: notesFirstColumnWidth } },
        { content: 'Note', styles: { halign: 'center', valign: 'bottom', cellWidth: noteColumnWidth } },
      ]

      notesColumns.forEach((branch, index) => {
        const branchColor = BRANCH_COLUMN_COLORS[index % BRANCH_COLUMN_COLORS.length]
        row.push({
          content: `${branch.label}\n( Rs. )`,
          styles: {
            halign: 'center',
            cellWidth: notesNumericColumnWidth,
            textColor: branchColor.text,
            fontSize: notesBranchHeaderFontSize,
          },
        })
      })

      row.push({
        content: 'Total\n( Rs. )',
        styles: {
          halign: 'center',
          cellWidth: notesNumericColumnWidth,
          textColor: [17, 24, 39],
          fontSize: notesBranchHeaderFontSize,
        },
      })

      return [row]
    }

    const firstLevelData = (data) => {
      let noteValue = 0

        return data.map((row) => ({
          account_description: row.name,
        balances: reportColumns.map((branch) => row[branch.key] || 0),
          total: row.total || 0,
        is_percentage: row.is_percentage || 0,
        is_label: row.is_label || 0,
        note: row.type === TYPE_GROUP ? ++noteValue : '',
        type: row.type,
        level: row.level,
        children: row.children && row.children.length > 0 ? row.children : [],
      }))
    }

    const bodyRows = (data) => {
      return firstLevelData(data).map((row) => {
        const columns = [{ content: row.account_description, styles: { halign: 'left' } }]

        row.balances.forEach((balance) => {
          columns.push({
            content: formatPdfValue(balance, row),
            styles: { halign: 'right', overflow: 'hidden' },
          })
        })

        columns.push({
          content: formatPdfValue(row.total, row),
          styles: { halign: 'right', overflow: 'hidden' },
        })

        columns.push({ content: row.type, styles: { display: 'none' } })
        return columns
      })
    }

    const createNotesData = (parentRow) => {
      const notesData = []

      if (parentRow.type === TYPE_GROUP) {
        notesData.push({
          account_description: `${parentRow.note}  ${parentRow.account_description.trim()}`,
          balances: notesColumns.map(() => ''),
          total: '',
          level: parentRow.level,
          note: '',
          rowToBold: true,
          rowToUnderline: false,
          is_percentage: parentRow.is_percentage || 0,
          type: parentRow.type,
        })

        if (parentRow.children && parentRow.children.length > 0) {
          let noteValue = 0

          parentRow.children
            .filter((child) => shouldKeepPdfRow(child, child.children || []))
            .forEach((child) => {
              notesData.push({
                account_description: '        ' + child.name,
                balances: layouts.map((branchLayout) => getPdfBranchAggregateValue(child, branchLayout)),
                total: child.total || 0,
                level: child.level,
                note: `${parentRow.note}.${++noteValue}`,
                type: child.type,
                rowToBold: false,
                rowToUnderline: false,
                is_percentage: child.is_percentage || 0,
                is_label: child.is_label || 0,
                children: child.children && child.children.length > 0 ? child.children : [],
              })
            })

          const totalChild = parentRow.children[parentRow.children.length - 1]
          notesData.push({
            account_description: '',
            balances: layouts.map((branchLayout) => getPdfBranchAggregateValue(parentRow, branchLayout)),
            total: parentRow.total || 0,
            level: totalChild ? totalChild.level : parentRow.level,
            note: '',
            rowToBold: true,
            rowToUnderline: true,
            is_percentage: parentRow.is_percentage || 0,
            is_label: parentRow.is_label || 0,
            type: totalChild ? totalChild.type : parentRow.type,
            children: [],
          })
        }
      }

      return notesData
    }

    const bodyRowsNotes = (data) => {
      let finalData = []
      const firstLevel = firstLevelData(data)

      for (let i = 0; i < firstLevel.length; i++) {
        if (firstLevel[i].level === 1) {
          const secondNote = createNotesData(firstLevel[i])
          finalData = finalData.concat(secondNote)

          for (let j = 0; j < secondNote.length; j++) {
            if (secondNote[j].level === 2) {
              const thirdNote = createNotesData(secondNote[j])
              finalData = finalData.concat(thirdNote)

              for (let k = 0; k < thirdNote.length; k++) {
                if (thirdNote[k].level === 3) {
                  const fourthNote = createNotesData(thirdNote[k])
                  finalData = finalData.concat(fourthNote)
                }
              }
            }
          }
        }
      }

      return finalData.map((row) => {
        const columns = [
          {
            content: row.account_description,
            styles: {
              halign: 'left',
              fontStyle: row.rowToBold ? 'bold' : 'normal',
              fontSize: row.rowToBold ? 10 : 9,
            },
          },
          {
            content: row.note,
            styles: { halign: 'center', fontStyle: 'bold', fontSize: 9 },
          },
        ]

        row.balances.forEach((balance) => {
          columns.push({
            content: balance !== '' ? formatPdfValue(balance, row) : '',
            styles: {
              halign: 'right',
              fontStyle: row.rowToBold ? 'bold' : 'normal',
              fontSize: 8,
              overflow: 'hidden',
            },
          })
        })

        columns.push({
          content: row.total !== '' ? formatPdfValue(row.total, row) : '',
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
            fontSize: 8,
            overflow: 'hidden',
          },
        })

        columns.push({ content: row.type, styles: { display: 'none' } })
        columns.push({ content: row.rowToUnderline, styles: { display: 'none' } })

        return columns
      })
    }

    const statementColumnStyles = {
      0: {
        halign: 'left',
        cellWidth: statementFirstColumnWidth,
        minCellHeight: 6,
        font: 'helvetica',
        fontSize: statementLabelFontSize,
      },
    }

    for (let i = 0; i < statementNumericColumnCount; i++) {
      statementColumnStyles[i + 1] = {
        halign: 'right',
        cellWidth: statementNumericColumnWidth,
        minCellHeight: 6,
        font: 'helvetica',
        fontSize: statementValueFontSize,
        overflow: 'hidden',
        cellPadding: { top: 1.2, right: 1, bottom: 1.2, left: 1 },
      }
    }

    const notesColumnStyles = {
      0: { halign: 'left', cellWidth: notesFirstColumnWidth, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
      1: { halign: 'center', cellWidth: noteColumnWidth, minCellHeight: 6, font: 'helvetica', fontStyle: 'bold', fontSize: 9 },
    }

    for (let i = 0; i < notesNumericColumnCount; i++) {
      notesColumnStyles[i + 2] = {
        halign: 'right',
        cellWidth: notesNumericColumnWidth,
        minCellHeight: 6,
        font: 'helvetica',
        fontSize: 7.5,
        overflow: 'hidden',
        cellPadding: { top: 0.8, right: 0.5, bottom: 0.8, left: 0.5 },
      }
    }

    doc.autoTable({
      head: statementHeadRows(),
      body: bodyRows(visibleReportData),
      theme: 'plain',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [60, 75, 100],
        fontSize: statementBranchHeaderFontSize,
      },
      columnStyles: statementColumnStyles,
      margin: { top: 40, bottom: 15, left: 10, right: 10 },
      didDrawPage: function () {
        header()
        footer()
      },
      willDrawCell: function (data) {
        if (data.row.section === 'head' && data.column.index > 0) {
          const { cell } = data
          doc.setDrawColor(0, 0, 0)
          doc.setLineWidth(1)
          doc.line(cell.x + 2, cell.y + cell.height, cell.x + cell.width - 2, cell.y + cell.height)
        }

        if (
          data.row.section === 'body' &&
          data.row.raw[statementNumericColumnCount + 1].content === TYPE_TOTAL &&
          data.column.index > 0
        ) {
          const { cell } = data
          doc.setDrawColor(0, 0, 0)
          doc.setLineWidth(0.2)
          doc.line(cell.x + 2, cell.y, cell.x + cell.width - 2, cell.y)
        }
      },
      didParseCell: function (data) {
        if (data.section === 'body' && data.column.index > 0 && data.column.index <= reportColumns.length) {
          data.cell.styles.textColor = BRANCH_COLUMN_COLORS[(data.column.index - 1) % BRANCH_COLUMN_COLORS.length].text
        }
        if (data.section === 'body' && data.column.index === reportColumns.length + 1) {
          data.cell.styles.textColor = [17, 24, 39]
        }
        if (data.section === 'body' && data.row.raw[statementNumericColumnCount + 1].content === TYPE_TOTAL) {
          data.cell.styles.fontSize = Math.max(statementValueFontSize, 7.5)
          data.cell.styles.fontStyle = 'bold'
        }
        if (data.section === 'head') {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    doc.addPage()

    doc.autoTable({
      head: notesHeadRows(),
      body: bodyRowsNotes(visibleReportData),
      theme: 'plain',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [60, 75, 100],
        fontSize: notesBranchHeaderFontSize,
      },
      columnStyles: notesColumnStyles,
      margin: { top: 40, bottom: 15, left: 10, right: 10 },
      didDrawPage: function () {
        headerNotes()
        footer()
      },
      willDrawCell: function (data) {
        if (data.row.section === 'head' && data.column.index > 1) {
          const { cell } = data
          doc.setDrawColor(0, 0, 0)
          doc.setLineWidth(1)
          doc.line(cell.x + 2, cell.y + cell.height, cell.x + cell.width - 2, cell.y + cell.height)
        }

        if (
          data.row.section === 'body' &&
          data.row.raw[notesNumericColumnCount + 3].content === true &&
          data.column.index > 1
        ) {
          const { cell } = data
          doc.setDrawColor(0, 0, 0)
          doc.setLineWidth(0.5)
          doc.line(cell.x + 2, cell.y, cell.x + cell.width - 2, cell.y)
          doc.line(cell.x + 2, cell.y + 6, cell.x + cell.width - 2, cell.y + 6)
        }
      },
      didParseCell: function (data) {
        if (
          data.section === 'body' &&
          data.column.index > 1 &&
          data.column.index <= notesColumns.length + 1
        ) {
          data.cell.styles.textColor = BRANCH_COLUMN_COLORS[(data.column.index - 2) % BRANCH_COLUMN_COLORS.length].text
        }
        if (data.section === 'body' && data.column.index === notesColumns.length + 2) {
          data.cell.styles.textColor = [17, 24, 39]
        }
        if (data.section === 'body' && data.row.raw[notesNumericColumnCount + 2].content === TYPE_TOTAL) {
          data.cell.styles.fontSize = Math.max(notesValueFontSize, 8.5)
          data.cell.styles.fontStyle = 'bold'
        }
        if (data.section === 'head') {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp)
    }

    const totalPages = doc.internal.getNumberOfPages()
    const pagesToStamp = totalPages > 1 ? [1, totalPages] : [1]

    pagesToStamp.forEach((pageNo) => {
      doc.setPage(pageNo)
      drawPrintedAt()
    })

    const pdfBlob = doc.output('blob')
    const pdfUrl = URL.createObjectURL(pdfBlob)
    window.open(pdfUrl, '_blank')
  }

  generatePdf(gpReportData, asOf, branchLayouts)
}

export default Pdf
